import { ButtonIcon, Table } from "mta-rkvr-styles";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { post, remove } from "../api/fetch";
import { RobotMessagesRequestBody } from "../api/HomePageRequestTypes";
import { RobotMessagesResponseBody } from "../api/HomePageResponseTypes";
import CurrentUserContext from "../CurrentUserContext";
import { ColumnHeader, RelationType } from "../types/homepage/RobotMessages";
import { RobotMessage } from "../types/homepage/RobotMessages";
import { PaginationRequest, Paginator } from "../types/util/TableFunctionalityTypes";
import { mapClassifiersValuesToLabels } from "../util/classifierManager";
import { toLocaleDate } from "../util/conversionUtils";
import { ADMINISTRATION, ASSETS, CONTRACTS, ORGANISATIONS, PROCEEDINGS } from "../util/paths";

const RobotMessages = () => {
  const { t } = useTranslation();
  const tBase = "homePageAuth.robotMessages.";

  const { id } = useContext(CurrentUserContext);

  const [messages, setMessages] = useState<RobotMessage[]>();

  const [latestRequest, setLatestRequest] = useState<{
    paginationRequest: PaginationRequest;
  }>({
    paginationRequest: {
      offset: 0,
      limit: 10,
    },
  });

  useEffect(() => {
    getMessages();
  }, []);

  const getMessages = (paginationRequest = latestRequest.paginationRequest) => {
    setLatestRequest({ paginationRequest });
    post<RobotMessagesRequestBody, RobotMessagesResponseBody>({
      endpoint: "robotMessages",
      requestBody: {
        kasutaja: id,
        ...paginationRequest,
      },
    }).then(async ({ body }) => {
      const { resultsList: messages } = body;
      const labelsObject = await mapClassifiersValuesToLabels([
        {
          type: "notifyType",
          values: messages.map((message) => message.tyyp),
        },
      ]);
      setMessages(
        body.resultsList.map((message) => {
          return {
            ...message,
            tyyp: {
              label: labelsObject.notifyType!.shift()!,
              value: message.tyyp,
            },
            seoseTyyp: RelationType[message.seoseTyyp as keyof typeof RelationType],
            lisatud: toLocaleDate(message.lisatud),
          };
        }),
      );
      setPaginator({
        elementOffset: body.offset,
        numberOfElements: body.resultCount,
        pageSize: body.limit,
        totalElements: body.totalCount,
      });
    });
  };

  const [paginator, setPaginator] = useState<Paginator>();
  const onPagination = (paginator: { pageSize: number; elementOffset: number }) => {
    getMessages({
      offset: paginator.elementOffset,
      limit: paginator.pageSize,
    });
  };

  const createLink = (link: string, type: RelationType, id: number) => {
    let path;
    switch (type) {
      case RelationType.KASUTUSLEPING:
        path = CONTRACTS;
        break;
      case RelationType.MENETLUS:
        path = PROCEEDINGS;
        break;
      case RelationType.ORGANISATION:
        path = ADMINISTRATION + ORGANISATIONS;
        break;
      case RelationType.VARAOBJECT:
        path = ASSETS;
        break;
    }

    return <Link to={`${path}/${id}`}>{link}</Link>;
  };

  const markDone = (id: number) => {
    remove<never>({ endpoint: "accountNotifications", pathParams: [id] }).then((res) => {
      res.ok && getMessages();
    });
  };

  return (
    <>
      {messages && (
        <Table
          id={t(tBase + "title")}
          title={t(tBase + "title")}
          headers={Object.keys(ColumnHeader).map((header) => {
            return t(tBase + header);
          })}
          content={messages?.map((message) => {
            const { allikas, link, lisatud, seos, teade, tyyp, seoseTyyp } = message;
            return {
              tyyp: tyyp.label,
              teade,
              link: link && seoseTyyp && seos && createLink(link, seoseTyyp, seos),
              allikas,
              lisatud,
            };
          })}
          translate={t}
          paginator={paginator}
          onPagination={onPagination}
          rowIds={messages.map((message) => message.yldteadeId)}
          editable
          customEditRowComponent={(obj: { rowId: number }) => (
            <ButtonIcon iconName={"icon-delete"} onClick={() => markDone(obj.rowId)} tooltip={t(tBase + "markDone")} />
          )}
        />
      )}
    </>
  );
};

export default RobotMessages;
